import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import apiService from "../../../service/Api";
import type { IPackage, IService } from "../../../types/package";
import { logout } from "../../../store/slice/AuthSlice";
import PackagesSection from "./components/PackageSelection";
import ServicesSection from "./components/ServicesSection";
import AddServiceModal from "./modals/AddService";
import { useDispatch } from "react-redux";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

const PackageData: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [activePackageTab, setActivePackageTab] = useState(0);

  const [packages, setPackages] = useState<IPackage[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [errorPackages, setErrorPackages] = useState<string | null>(null);
  const [packagePage, setPackagePage] = useState(1);
  const [packageCount, setPackageCount] = useState(0);
  const [showPublicOnly, setShowPublicOnly] = useState(false);

  const [activeServiceTab, setActiveServiceTab] = useState(0);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<IService | null>(null);
  const [isServiceEditMode, setIsServiceEditMode] = useState(false);
  const [services, setServices] = useState<IService[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [errorServices, setErrorServices] = useState<string | null>(null);
  const [servicePage, setServicePage] = useState(1);
  const [serviceCount, setServiceCount] = useState(0);
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState("");

  const packageTypeMap = useMemo<Record<number, string>>(
    () => ({
      0: "Funeral",
      1: "FuneralHome",
      2: "Church",
      3: "Conference",
    }),
    []
  );

  const serviceTabs = useMemo(
    () => ["Screens", "Video & Audio", "AV Equipment", "Design", "Other", "Old Mutual"],
    []
  );

  const fetchPackages = useCallback(async () => {
    setIsLoadingPackages(true);
    setErrorPackages(null);
    try {
      const selectedType = packageTypeMap[activePackageTab];
      const publicFilter = showPublicOnly ? "&show_on_public=true" : "";
      const packageResponse = await apiService.get<{
        count: number;
        results: IPackage[];
      }>(
        `/api/packages/package/?package_type=${selectedType}&page=${packagePage}${publicFilter}`
      );
      const fetchedPackages = packageResponse.data.results || [];
      setPackageCount(packageResponse.data.count);
      setPackages(fetchedPackages);
    } catch (err: unknown) {
      const errorResponse = (err as {
        response?: { status?: number; data?: { message?: string } };
      })?.response;
      let message =
        errorResponse?.data?.message ||
        "Failed to load packages. Please try again.";
      if (errorResponse?.status === 401) {
        message = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      }
      setErrorPackages(message);
    } finally {
      setIsLoadingPackages(false);
    }
  }, [activePackageTab, packagePage, showPublicOnly, dispatch, navigate, packageTypeMap]);

  const fetchServices = useCallback(async () => {
    setIsLoadingServices(true);
    setErrorServices(null);
    try {
      const selectedServiceCategory = serviceTabs[activeServiceTab];
      const categoryFilter = serviceCategoryFilter
        ? `&category=${encodeURIComponent(serviceCategoryFilter)}`
        : "";
      const serviceResponse = await apiService.get<{
        count: number;
        results: IService[];
      }>(
        `/api/packages/package-service/?service_category=${encodeURIComponent(
          selectedServiceCategory
        )}&page=${servicePage}${categoryFilter}`
      );
      const fetchedServices = serviceResponse.data.results || [];
      setServiceCount(serviceResponse.data.count);
      setServices(
        fetchedServices.map((service) => ({
          ...service,
          variation: service.variation ?? "--",
          status: service.status ?? "Active",
        }))
      );
    } catch (err: unknown) {
      const errorResponse = (err as {
        response?: { status?: number; data?: { message?: string } };
      })?.response;
      let message =
        errorResponse?.data?.message ||
        "Failed to load services. Please try again.";
      if (errorResponse?.status === 401) {
        message = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      }
      setErrorServices(message);
    } finally {
      setIsLoadingServices(false);
    }
  }, [activeServiceTab, servicePage, serviceCategoryFilter, dispatch, navigate, serviceTabs]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleServiceAdded = () => {
    setServicePage(1);
    fetchServices();
  };

  return (
    <Box>
      <PackagesSection
        activeTab={activePackageTab}
        onTabChange={(index) => {
          setActivePackageTab(index);
          setPackagePage(1);
        }}
        packages={packages}
        isLoading={isLoadingPackages}
        error={errorPackages}
        onRetry={fetchPackages}
        onAddPackage={hasPermission(PERMISSIONS.CONFIGURATION_ADD) ? () => {
          navigate("/configurations/package/new");
        } : undefined}
        onCreateCombo={hasPermission(PERMISSIONS.CONFIGURATION_ADD) ? () => {
          navigate("/configurations/package/new");
        } : undefined}
        onEditPackage={(pkg) => {
          navigate(`/configurations/package/edit/${pkg.id}`);
        }}
        currentPage={packagePage}
        totalCount={packageCount}
        onNextPage={() => setPackagePage((prev) => prev + 1)}
        onPreviousPage={() => setPackagePage((prev) => Math.max(prev - 1, 1))}
        showPublicOnly={showPublicOnly}
        onTogglePublicOnly={(checked) => {
          setShowPublicOnly(checked);
          setPackagePage(1);
        }}
      />

      <ServicesSection
        activeTab={activeServiceTab}
        onTabChange={(index) => {
          setActiveServiceTab(index);
          setServicePage(1);
        }}
        services={services}
        isLoading={isLoadingServices}
        error={errorServices}
        onRetry={fetchServices}
        onAddService={hasPermission(PERMISSIONS.CONFIGURATION_ADD) ? () => {
          setSelectedService(null);
          setIsServiceEditMode(false);
          setAddServiceOpen(true);
        } : undefined}
        onEditService={(service) => {
          setSelectedService(service);
          setIsServiceEditMode(true);
          setAddServiceOpen(true);
        }}
        currentPage={servicePage}
        totalCount={serviceCount}
        onNextPage={() => setServicePage((prev) => prev + 1)}
        onPreviousPage={() => setServicePage((prev) => Math.max(prev - 1, 1))}
        categoryFilter={serviceCategoryFilter}
        onCategoryFilterChange={(value) => {
          setServiceCategoryFilter(value);
          setServicePage(1);
        }}
      />

      <AddServiceModal
        open={addServiceOpen}
        onClose={() => {
          setAddServiceOpen(false);
          setSelectedService(null);
          setIsServiceEditMode(false);
        }}
        onServiceAdded={handleServiceAdded}
        selectedService={selectedService}
        isEditMode={isServiceEditMode}
      />
    </Box>
  );
};

export default PackageData;
